import { createSlice } from '@reduxjs/toolkit';
import { createIndependentStore } from '@utils/react';

const initialState = {
  isEditing: false,
  disabledIds: {}
};
const slice = createSlice({
  name: "feature/course-revisions",
  initialState,
  reducers: {
    reset(state) {
      state.isEditing = initialState.isEditing;
      state.disabledIds = initialState.disabledIds;
    },
    toggleEditMode(state, action) {
      state.isEditing = action.payload.state ?? !state.isEditing;

      // If we've entered edit mode, then ensure that we update the state to
      // match.
      if (state.isEditing) {
        state.editableRevision = action.payload.editedRevision;
        state.disabledIds = action.payload.editedRevision?.disabledContentIds ?? {};
      }
    },
    /**
     * Toggling elements on a page becomes a bit more involved than simply
     * toggling the node itself.
     * In particular we need to identify whether or not all nodes have been
     * toggled off, and if they have, then the page itself should also be
     * toggled off.
     * If at least one node is enabled on a page, then the page itself should
     * also be enabled.
     */
    toggleElement(state, action) {
      if (!state.isEditing) {
        console.warn("courseRevisionsStore().toggleElement(): Cannot be called when state.isEditing = false");
        return;
      }
      const chapterWasDisabled = state.disabledIds[action.payload.chapterId]?.disabled;
      const nodeIds = extractNodeIdsFromAST(action.payload.pageAST);
      const nodeWasPreviouslyDisabled = state.disabledIds[action.payload.chapterId]?.disabled || state.disabledIds[action.payload.chapterId]?.[action.payload.elementId];

      // In case the chapter was disabled on chapter level, then ensure that
      // all elements are toggled as disabled before perform logic on individual
      // element level...
      if (chapterWasDisabled) {
        state.disabledIds[action.payload.chapterId] = {
          disabled: false,
          ...Object.fromEntries(nodeIds.map(it => [it, true]))
        };
      }

      // ... and then the individual node can be toggled as normal.
      state.disabledIds[action.payload.chapterId] = {
        ...state.disabledIds[action.payload.chapterId],
        [action.payload.elementId]: action.payload.disabled ?? !nodeWasPreviouslyDisabled
      };
      const shouldChapterBeDisabled = nodeIds.every(nodeId => state.disabledIds[action.payload.chapterId]?.[nodeId]);

      // ... finally, if the chapter should be disabled due to all children
      // being toggled off, then we can simplify our state by simply setting the
      // entire chapter as disabled.
      if (shouldChapterBeDisabled) {
        state.disabledIds[action.payload.chapterId] = {
          disabled: true
        };
      }
    },
    toggleChapter(state, action) {
      if (!state.isEditing) {
        console.warn("courseRevisionsStore().toggleChapter(): Cannot be called when state.isEditing = false");
        return;
      }
      state.disabledIds[action.payload.chapterId] = {
        disabled: action.payload.disabled ?? !state.disabledIds[action.payload.chapterId]?.disabled
      };
    }
  }
});
const courseRevisionsStore = createIndependentStore(slice);
const useCourseRevisionState = courseRevisionsStore.useSelector;
function extractNodeIdsFromAST(roots) {
  return roots.flatMap(node => {
    const nodeId = "id" in node ? node.id : undefined;
    const children = "children" in node && node.children ? node.children : [];
    const childrenIds = ["grid-component", "grid-component.column", "accordion-component", "accordion-component.panel"].includes(node.nodeType) ? extractNodeIdsFromAST(children) : [];
    return [nodeId, ...childrenIds];
  }).filter(it => !!it);
}

export { courseRevisionsStore, extractNodeIdsFromAST, slice, useCourseRevisionState };
//# sourceMappingURL=store.js.map
